import { useReducer, useCallback, useRef, useEffect } from 'react';
import { 
  ExpressionGroupsState, 
  Expression, 
  AdvancedExpressionBuilderProps,
  ApiConditions
} from '../types';
import { expressionReducer, getInitialState } from '../expressionReducer';

// Generate unique IDs for groups and expressions
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export interface UseAdvancedExpressionBuilderReturn {
  // Current state
  state: ExpressionGroupsState;
  
  // Group operations
  createGroup: () => void;
  removeGroup: (groupId: string) => void;
  switchGroupsOperator: (operator: 'AND' | 'OR') => void;
  
  // Expression operations
  createExpression: (groupId: string) => void;
  removeExpression: (groupId: string, expressionId: string) => void;
  updateExpression: (groupId: string, expressionId: string, expression: Partial<Expression>) => void;
  switchExpressionsOperator: (groupId: string, operator: 'AND' | 'OR') => void;
  
  // State operations
  resetState: () => void;
  
  // API transformation
  transformToApi: () => ApiConditions;
}

interface UseAdvancedExpressionBuilderParams {
  initialExpressions?: ExpressionGroupsState;
  onChange: AdvancedExpressionBuilderProps['onChange'];
}

export const useAdvancedExpressionBuilder = ({
  initialExpressions,
  onChange,
}: UseAdvancedExpressionBuilderParams): UseAdvancedExpressionBuilderReturn => {
  // Initialize state with either provided initial expressions or default state
  const [state, dispatch] = useReducer(expressionReducer, initialExpressions || getInitialState());
  
  // Use ref to control when onChange is called - only when expressions are completed
  const previousStateRef = useRef<ExpressionGroupsState | null>(null);

  // Check if all expressions in the state are completed
  const areAllExpressionsCompleted = useCallback((currentState: ExpressionGroupsState): boolean => {
    const groups = Object.values(currentState.groups || {});
    
    if (groups.length === 0) return false;
    
    return groups.every(group => {
      const expressions = Object.values(group.expressions);
      return expressions.length > 0 && expressions.every(expr => expr.isCompleted);
    });
  }, []);

  // Set initial state if provided
  useEffect(() => {
    if (initialExpressions && initialExpressions.groups) {
      dispatch({ type: 'SET_INITIAL_STATE', payload: { state: initialExpressions } });
    }
  }, [initialExpressions]);

  // Call onChange when state changes and all expressions are completed
  useEffect(() => {
    if (previousStateRef.current !== null && areAllExpressionsCompleted(state)) {
      onChange(state);
    }
    previousStateRef.current = state;
  }, [state, onChange, areAllExpressionsCompleted]);

  // Group operations
  const createGroup = useCallback(() => {
    const groupId = generateId();
    dispatch({ type: 'CREATE_GROUP', payload: { groupId } });
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    dispatch({ type: 'REMOVE_GROUP', payload: { groupId } });
  }, []);

  const switchGroupsOperator = useCallback((operator: 'AND' | 'OR') => {
    dispatch({ type: 'SWITCH_GROUPS_OPERATOR', payload: { operator } });
  }, []);

  // Expression operations
  const createExpression = useCallback((groupId: string) => {
    const expressionId = generateId();
    dispatch({ type: 'CREATE_EXPRESSION', payload: { groupId, expressionId } });
  }, []);

  const removeExpression = useCallback((groupId: string, expressionId: string) => {
    dispatch({ type: 'REMOVE_EXPRESSION', payload: { groupId, expressionId } });
  }, []);

  const updateExpression = useCallback((groupId: string, expressionId: string, expression: Partial<Expression>) => {
    dispatch({ type: 'UPDATE_EXPRESSION', payload: { groupId, expressionId, expression } });
  }, []);

  const switchExpressionsOperator = useCallback((groupId: string, operator: 'AND' | 'OR') => {
    dispatch({ type: 'SWITCH_EXPRESSIONS_OPERATOR', payload: { groupId, operator } });
  }, []);

  // State operations
  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  // Transform internal state to API format
  const transformToApi = useCallback((): ApiConditions => {
    // Build the expressions array - flattened list of all expressions
    const expressions: any[] = [];
    
    Object.entries(state.groups || {}).forEach(([groupId, group]) => {
      Object.entries(group.expressions).forEach(([expressionId, expression]) => {
        if (expression.isCompleted && expression.field && expression.operator) {
          // Build the condition definition for the API
          const conditionDefinition: any = {
            id: expressionId,
            groupId: groupId,
            field: expression.field.identifier,
            type: expression.field.fieldType,
            operator: expression.operator,
            value: expression.value,
            fieldType: expression.field.fieldType,
          };

          // Handle special cases for different operators and field types
          if (expression.operator === 'between' && expression.value.length === 2) {
            conditionDefinition.value = {
              from: expression.value[0],
              to: expression.value[1],
            };
          } else if (expression.field.fieldType === 'multiselect') {
            conditionDefinition.value = Array.isArray(expression.value) ? expression.value : [expression.value];
          } else if (expression.field.fieldType === 'boolean') {
            conditionDefinition.value = expression.value[0] === 'true' || expression.value[0] === true;
          } else {
            conditionDefinition.value = expression.value[0];
          }

          expressions.push(conditionDefinition);
        }
      });
    });

    return {
      expressions,
      expressionsStructure: state,
    };
  }, [state]);

  return {
    state,
    createGroup,
    removeGroup,
    switchGroupsOperator,
    createExpression,
    removeExpression,
    updateExpression,
    switchExpressionsOperator,
    resetState,
    transformToApi,
  };
};